import { createContext, useCallback, useContext } from 'react';

import { Account } from '@/workspace/types';

const trimPath = (path: string) => (path[0] === '/' ? path.slice(1) : path);

export const prependSlug = (slug: string, url: string) => `/${slug}/${trimPath(url)}`;

export interface AccountContext {
  account: Account;
  accounts: Account[];
  enabledFeatures: string[];
  switchAccount(accountId: string): void;
  hasPermission(permission: string): boolean;
  isFeatureEnabled(feature: string): boolean;
}

const defaultContextValue = Symbol();

export const AccountContext = createContext<AccountContext | typeof defaultContextValue>(
  defaultContextValue,
);

export const useAccountContext = () => {
  const context = useContext(AccountContext);
  if (context === defaultContextValue) {
    throw new Error('useAccountContext must be used within a AccountContextProvider');
  }
  return context;
};
export function useSelectedAccount() {
  const accountData = useAccountContext();
  return accountData.account;
}

export function useAccountTimezone() {
  const { account } = useAccountContext();
  return account.timezone ?? 'UTC';
}

export function useBuildAccountUrl() {
  const accountData = useAccountContext();
  return useCallback(
    (url: string) => prependSlug(accountData.account.slug, url),
    [accountData.account.slug],
  );
}
